import React from "react";
import VehicleCard from "../../components/VehicleCard";
import {
  fetchAllPrices,
  fetchDailyEarn,
  fetchMonthlyEarn,
  fetchRecentTrips,
} from "../../services/balance";
import { CardsContainer, TablesContainer } from "./style";
import { useQuery } from "@tanstack/react-query";
import TableTitle from "../../components/TableTitle";
import DataTable from "../../components/DataTable";
import Spinner from "../../components/Spinner";
import StarRating from "../../components/TableTitle/StarRating";

const earns = [
  "التاريخ",
  "عدد الرحلات",
  "عدد الساعات",
  "كم",
  "الربح الصافي",
  "الربح الكلي",
];

const recentTrips = [
  "ID",
  "اسم العميل",
  "اسم السائق",
  "تاريخ الرحلة",
  "عددالدقائق",
  "كم",
  "الربح",
  "قيمة الرحلة",
  "نوع الرحلة",
  "تصنيف المركبة",
  "تقييم السائق",
  "تقييم العميل",
];

const tripTypeMap = {
  SCHEDULED: "مُجدولة",
  IMMEDIATE: "فورية",
};

const mapRecentRows = (trips) =>
  trips.map((t, idx) => {
    const driverName = t.driver?.user?.name ?? "—";
    const carClass = t.category ?? "—";
    const tripType = tripTypeMap[t.type] ?? "—";
    const km = t.total_distance?.toFixed(2) ?? "—";
    const minutes = t.expected_duration.toFixed(2);
    const amount = t.total_price ?? 0;
    const profit = t.profit?.toFixed(2) ?? "—";
    const tripDate = t.start_time
      ? new Date(t.start_time).toLocaleDateString("ar-EG")
      : "—";
    const customer = t.user?.name ?? "—";
    const customerRate = t.rating?.user_rating;
    const driverRate = t.rating?.driver_rating;
    const num = idx + 1;
    return [
      num,
      customer,
      driverName,
      tripDate,
      minutes,
      km,
      profit,
      amount.toFixed(2),
      tripType,
      carClass,
      driverRate ? (
        <StarRating rating={driverRate} />
      ) : (
        <StarRating rating={0} />
      ),
      customerRate ? (
        <StarRating rating={customerRate} />
      ) : (
        <StarRating rating={0} />
      ),
      "",
    ];
  });

const mapDailyRows = (list) =>
  list.map((d) => {
    const { year, month, day } = d._id;
    const dateObj = new Date(year, month - 1, day);
    const dateStr = dateObj.toLocaleDateString("ar-EG");

    return [
      dateStr,
      d.totalTrips,
      (d.totalDurationMinutes / 60).toFixed(1),
      d.totalDistanceKilo.toFixed(2),
      d.totalProfit.toFixed(2),
      d.totalEarnings.toFixed(2),
      "",
    ];
  });

const mapMonthlyRows = (list) =>
  list.map((d) => {
    const monthStr = `${d._id.month} الشهر`;
    return [
      monthStr,
      d.totalTrips,
      d.totalProfit.toFixed(2),
      d.totalDistanceKilo.toFixed(2),
      (d.totalDurationMinutes / 60).toFixed(1),
      d.totalEarnings.toFixed(2),
      "",
    ];
  });

const Balances = () => {
  const {
    data,
    error: priceError,
    isLoading: priceLoading,
    isError: isPriceError,
  } = useQuery({
    queryKey: ["prices"],
    queryFn: fetchAllPrices,
  });

  const {
    data: dailyEarn,
    error: dailyError,
    isLoading: dailyLoading,
    isError: isDailyError,
  } = useQuery({
    queryKey: ["dailyEarn"],
    queryFn: fetchDailyEarn,
  });

  const {
    data: monthlyEarn,
    error: monthlyError,
    isLoading: monthlyLoading,
    isError: isMonthlyError,
  } = useQuery({
    queryKey: ["monthlyEarn"],
    queryFn: fetchMonthlyEarn,
  });

  const {
    data: tripsData,
    error: tripsError,
    isLoading: tripsLoading,
    isError: isTripsError,
  } = useQuery({ queryKey: ["recentTrips"], queryFn: fetchRecentTrips });

  if (priceLoading || dailyLoading || monthlyLoading || tripsLoading)
    return <Spinner />;

  if (isPriceError) return <p>خطأ في الأسعار: {priceError.message}</p>;
  if (isDailyError) return <p>خطأ في الربح اليومي: {dailyError.message}</p>;
  if (isMonthlyError) return <p>خطأ في الربح الشهري: {monthlyError.message}</p>;
  if (isTripsError) return <p>خطأ في الرحلات: {tripsError.message}</p>;

  const prices = data?.prices ?? [];
  const recentRows = mapRecentRows(tripsData?.trips ?? []);
  const dailyRows = mapDailyRows(dailyEarn?.data?.dailyEarn ?? []);
  const monthlyRows = mapMonthlyRows(monthlyEarn?.data?.monthlyEarn ?? []);

  return (
    <>
      <CardsContainer>
        {prices &&
          prices.map((item, index) => (
            <VehicleCard
              key={index}
              type={item.car_class}
              pricePerKm={item.price_per_distance}
              profitPercent={`${item.profit_rate * 100}%`}
              pricePerHour={item.price_per_hour}
            />
          ))}
      </CardsContainer>
      {recentRows.length > 0 ? (
        <div style={{ marginTop: "2rem" }}>
          <TableTitle title={"الرحلات الحالية"} />
          <DataTable columns={recentTrips} data={recentRows} />
        </div>
      ) : (
        <div style={{ marginTop: "2rem" }}>
          <TableTitle title={"الرحلات الحالية"} />
          <p
            style={{
              fontSize: "18px",
              color: "#777",
              margin: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            🚗 لا يوجد رحلات حالية بعد
          </p>
        </div>
      )}
      <TablesContainer>
        {dailyRows.length > 0 ? (
          <div>
            <TableTitle title={"الربح اليومي"} />
            <DataTable columns={earns} data={dailyRows} />
          </div>
        ) : (
          <div>
            <TableTitle title={"الربح اليومي"} />
            <p
              style={{
                fontSize: "18px",
                color: "#777",
                margin: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              🚗 لا يوجد ربح يومي حتى الآن
            </p>
          </div>
        )}
        {monthlyRows.length > 0 ? (
          <div>
            <TableTitle title={"الربح الشهري"} />
            <DataTable columns={earns} data={monthlyRows} />
          </div>
        ) : (
          <div>
            <TableTitle title={"الربح الشهري"} />
            <p
              style={{
                fontSize: "18px",
                color: "#777",
                margin: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              🚗 لا يوجد ربح شهري حتى الآن
            </p>
          </div>
        )}
      </TablesContainer>
    </>
  );
};

export default Balances;
